<template>
  <tinymce
    v-if="initialized"
    v-model="content"
    api-key="ol1x2psd36c8v9qraeqwp7u9wrzvk39fxswb2dkyp7qfoebq"
    :init="init"
    :disabled="disabled"
  />
</template>

<script>
  import { mapState } from "vuex";

  import {
    GET_PROPERTY_MESSAGE_TEMPLATES_COLLECTION,
  } from "@/store/property-message-templates/actions";

  import {
    GET_PROPERTY_MESSAGE_TEMPLATES_VARIABLES,
    GET_PROPERTY_MESSAGE_TEMPLATES_EMAIL_PRESETS,
  } from "@/store/property-message-templates/actions";

  import tinymce from '@tinymce/tinymce-vue';

  const collect = (items, options) => {
    let result = [];

    if (!items.length) {
      return [
        {
          type: 'menuitem',
          text: 'No Results',
        }
      ]
    }

    options = options || {};

    const text = options.text || 'text';
    const type = options.type || 'menuitem';

    items.map((item) => {
      result.push({
        type: type,
        text: item[text],
        onAction () {
          options.action(item);
        }
      },)
    });

    return result;
  };

  export default {
    name: "VEditor",
    components: {
      tinymce
    },
    props: {
      value: {
        default: null
      },
      locale: {
        required: false,
        default: 'en'
      },
      placeholder: {
        type: String,
        required: false,
        default: ''
      },
      propertyId: {
        type: Number,
        required: false
      },
      disabled: {
        type: Boolean,
        required: false,
        default: false
      },
      height: {
        type: Number,
        required: false,
        default: 300
      },
      withReplacementTags: {
        type: Boolean,
        required: false,
        default: false
      },
      replacementTagsArray: {
        type: Array,
        required: false
      },
      withMessageTemplates: {
        type: Boolean,
        required: false,
        default: false
      },
      messageTemplatesArray: {
        type: Array,
        required: false
      },
      withEmailPresets: {
        type: Boolean,
        required: false,
        default: true
      },
      reservationId: {
        type: Number,
        required: false,
      },
      pasteDataImages: {
        type: Boolean,
        default: false
      }
    },
    created() {
      this.content = this.value;
    },
    beforeMount() {
      this.getEmailPresets();
    },
    data() {
      return {
        content: null,
        plugins: [
          //'advlist',
          //'autolink',
          'lists',
          'link',
          'image',
          //'charmap',
          //'print',
          'preview',
          //'anchor',
          'searchreplace',
          //'visualblocks',
          'code',
          'fullscreen',
          //'insertdatetime',
          'table',
          'code',
          //'help',
          //'wordcount',
          'paste',
          'template',
          //'media',
          // 'fullpage',
        ],
        toolbar: [
          'fullscreen',
          //'template',
          //'undo redo',
          'formatselect',
          'bold italic backcolor',
          'alignleft aligncenter alignright alignjustify',
          'bullist numlist outdent indent',
          'removeformat',
        ],
      }
    },
    methods: {
      setup(editor) {
        if (this.withReplacementTags) {
          this.setupReplacementTags(editor, this);
        }
        if (this.withMessageTemplates) {
          this.setupMessageTemplates(editor, this);
        }
      },
      getEmailPresets() {
        if (!this.withEmailPresets) {
          return;
        }
        this.$store.dispatch(GET_PROPERTY_MESSAGE_TEMPLATES_EMAIL_PRESETS);
      },
      setupReplacementTags(editor, instance) {
        editor.ui.registry.addMenuButton('replacementtags', {
          text: instance.$lang('property-message-templates.replacement_tags'),
          fetch(callback) {
            const action = (item) => {
              editor.insertContent(item.value);
              instance.$emit('replacement-tag-changed', item);
            };

            if (instance.replacementTagsInitialized) {
              callback(collect(instance.replacementTags, { action }));
            } else {
              let params = {};

              if (instance.replacementTagsArray) {
                  params = {
                    groups: instance.replacementTagsArray
                  }
              }

              instance.$store.dispatch(GET_PROPERTY_MESSAGE_TEMPLATES_VARIABLES, params)
                .then((response) => {
                  callback(collect(response, { action }));
                });
            }
          }
        });
      },
      setupMessageTemplates(editor, instance) {
        editor.ui.registry.addMenuButton('messagetemplates', {
          text: instance.$lang('property-message-templates.title'),
          fetch(callback) {
            instance.$store.dispatch(GET_PROPERTY_MESSAGE_TEMPLATES_COLLECTION, {
              sortBy: 'name',
              order: 'asc',
              types: instance.messageTemplatesArray,
              content_type: instance.$const('ConstMessageContentTypes', 'EMAIL'),
              property_id: instance.propertyId,
              reservation_id: instance.reservationId,
              _data: {
                selects: [
                  'name',
                  'subject',
                  'body',
                ]
              }
            })
              .then(({collection}) => {
                callback(collect(collection, {
                  text: 'name',
                  value: 'body',
                  action(item) {
                    const content = item.body[instance.locale ? instance.locale : 'en'] || '';
                    editor.insertContent(content);
                    instance.$emit('message-template-changed', item);
                  }
                }));
              });
          }
        });
      },
    },
    computed: {
      init() {
        if (this.withReplacementTags) {
          this.toolbar.unshift('replacementtags');
        }
        if (this.withMessageTemplates) {
          this.toolbar.unshift('messagetemplates');
        }

        let plugins = this.plugins.join(' ');
        let toolbar = this.toolbar.join('|');

        return {
          plugins,
          toolbar,
          setup: this.setup,
          height: this.height,
          placeholder: this.placeholder,
          language: this.$locale(),
          templates: this.presets,
          image_advtab: true,
          paste_data_images: this.pasteDataImages,
          visual: false,
        }
      },
      presets() {
        if (this.emailPresets.data && !this.emailPresets.data.length) {
          return [];
        }

        return this.emailPresets.data;
      },
      initialized() {
        if (this.emailPresets && this.emailPresets.loading) {
          return false;
        }

        return true;
      },
      ...mapState({
        replacementTags: state => state.propertyMessageTemplates.getVariablesRequest.data,
        replacementTagsInitialized: state => state.propertyMessageTemplates.getVariablesRequest.success,
        emailPresets: state => state.propertyMessageTemplates.getEmailPresetsRequest,
      })
    },
    watch: {
      value() {
        this.content = this.value;
      },
      content(val) {
        this.$emit('input', val);
      }
    }
  }
</script>

<style scoped>

</style>
